import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import "./style.scss";
import { useCookies } from "react-cookie";
import { useDispatch } from "react-redux";
import {
  fetchCommonData,
  getAllUsers,
  getOutletsData,
  saveAllOutletsStatus,
} from "../../../redux/slices";
import { useSelector } from "react-redux";
import { groupDataByKey } from "../../../helpers/catalog-helper";
import { cloneDeep, pick, set } from "lodash";
import BulkActionConfirmationModal from "../bulk-action-confirmation-modal";
import OutletActionHeader from "../outlet-action-header";
import OutletViewComponent from "../outlets-view";
import OutletConfigurationEditModal from "../outlet-configurations-edit-modal";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { MESSAGE_TYPE, outletStatus } from "../../../data/constants";
import { METHODS } from "../../../data/api-method-constants";
import { ENDPOINT } from "../../../services/apiUrls";
import moment from "moment";
import { isSourcePos } from "../../../helpers/common-helper";
import { isAdminUser } from "../../../helpers/user-info-helpers";
import { OUTLET_STATUSES } from "../../../data/outlet-status-constants";

const OutletStatusComponent: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [outletGroupByBranchMap, setOutletGroupByBranchMap] = useState<any>({}),
    [selectedBranches, setSelectedBranches] = useState<any>([]),
    [searchData, setSearchData] = useState<any>({
      value: "",
      count: 0,
    }),
    [bulkActionConfig, setBulkActionConfig] = useState<any>({
      type: "",
      active: false,
      selectedOutletsMap: {},
      showConfirmationModal: false,
    }),
    [isAddNewConfigModalOpen, setIsAddNewConfigModalOpen] = useState(false),
    [apiStateModalData, setApiStateModalData] = useState<any>({
      show: false,
      modalText: "",
    }),
    [updatedOutletsConfig, setUpdatedOutletsConfig] = useState<any>({
      showPill: false,
      updatedOutlets: {},
    }),
    [cookies, setCookie, removeCookie] = useCookies<any>([]),
    [loading, setLoading] = useState<boolean>(true);

  const { outletsData } = useSelector((state: any) => ({
    outletsData: state.outletStatusReducer.outletsData,
  }));
  const businessData = useSelector(
    (state: any) => state?.businessReducer?.businessData
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllUsers());
    fetchAllOutletsData(true);
    // fetchAllOutletsStatus();
  }, []);

  useEffect(() => {
    if (businessData?.branchId) {
      handleSelectedBranch();
    }
  }, [businessData?.branchId]);

  useEffect(() => {
    if (outletsData?.providers?.length) {
      const allOutletsList = outletsData?.providers?.length
        ? outletsData?.providers
        : [];
      let searchResults = getSearchResults(searchData?.value, allOutletsList);
      let groupedResults = groupDataByKey(searchResults, "brid") || {}; // group outlets by branch id
      let filteredResults = pick(groupedResults, selectedBranches); // filter results by selected branches
      setOutletGroupByBranchMap(filteredResults);
    } else {
      setOutletGroupByBranchMap({});
    }
  }, [selectedBranches, searchData?.value, outletsData?.providers]);

  const onApiStateModalClose = () => {
    setApiStateModalData({ show: false });
    if (updatedOutletsConfig?.showPill) {
      setTimeout(() => {
        setUpdatedOutletsConfig({ showPill: false });
      }, 3000);
    }
  };

  /**
   * On search input it will parse the outletList and it will filter
   * out all the outlets based on the search string
   * @param searchStr
   */
  const getSearchResults = (searchStr: string, outletsList: any) => {
    let results: any = [];
    if (searchStr) {
      results = outletsList.filter((outletObj: any) => {
        let matchesToChannelName: boolean = outletObj?.channelDefName
          ?.toLocaleLowerCase()
          ?.includes(searchStr.toLocaleLowerCase());
        let matchesToOutletId: boolean = outletObj?.id
          ?.toLocaleLowerCase()
          ?.includes(searchStr.toLocaleLowerCase());
        let matchesToBranchCode: boolean = outletObj?.branchCode
          ?.toLocaleLowerCase()
          ?.includes(searchStr.toLocaleLowerCase());
        let matchesToBranchName: boolean = outletObj?.branchName
          ?.toLocaleLowerCase()
          ?.includes(searchStr.toLocaleLowerCase());
        return (
          matchesToChannelName ||
          matchesToOutletId ||
          matchesToBranchCode ||
          matchesToBranchName
        );
      });
    } else {
      results = outletsList;
    }
    return results;
  };

  /**
   * Returns the number of outlets in branchOutletMap
   */
  function getBranchOutletsCount(branchOutletMap: any) {
    let result = Object.values(branchOutletMap).reduce(
      (countAc: number, outletsMap: any) => {
        return Object.keys(outletsMap)?.length
          ? countAc + Object.keys(outletsMap)?.length
          : countAc;
      },
      0
    );
    return result;
  }

  function flatBranchOutletsGroup(branchOutletGroup: any) {
    let branchOutletGroupClone = cloneDeep(branchOutletGroup || {});
    if (!Object.keys(branchOutletGroupClone)?.length) return [];
    let resultList: any = [];
    for (let branchId of Object.keys(branchOutletGroupClone)) {
      let outletsMap = branchOutletGroupClone[branchId];
      if (!Object.keys(outletsMap)?.length) continue;
      let branchOutletList = Object.values(outletsMap) || [];

      resultList = [...resultList, ...branchOutletList];
    }
    return resultList;
  }

  function fetchAllOutletsData(saveOutletStatus = false) {
    let accountNumber = businessData?.accountUUID;
    let cookieData =
      cookies?.outletStatus && cookies?.outletStatus[accountNumber];
    const dataToPost = {
      aggregatorType: "Order",
      branches: "all",
      // isNew: true,
    };
    dispatch(
      getOutletsData({
        dataToPost,
        saveStatus: !!cookieData || saveOutletStatus,
        callback: (res: any) => {
          setLoading(false);
        },
      })
    );
  }

  function fetchAllOutletsStatus() {
    let accountNumber = businessData?.accountUUID;
    let cookieData =
      cookies?.outletStatus && cookies?.outletStatus[accountNumber];
    if (!cookieData) {
      dispatch(
        fetchCommonData({
          method: METHODS.GET,
          url: ENDPOINT.OUTLET_STATUS.getAllOutletStatus,
          callback: saveOutletStatus,
        })
      );
    }
  }

  function saveOutletStatus(res: any) {
    setLoading(false);
    if (res.code) {
      fetchAllOutletsData(true);
      return;
    }
    let outletsData = {};
    Object.entries(res).forEach((val: any) => {
      let status = val[1];
      if (status === OUTLET_STATUSES.INACTIVE) status = OUTLET_STATUSES.OFFLINE;
      else if (status === OUTLET_STATUSES.ACTIVE)
        status = OUTLET_STATUSES.ONLINE;
      outletsData = Object.assign(outletsData, {
        [val[0]]: { currentStatus: status, lastChecked: new Date() },
      });
    });
    dispatch(saveAllOutletsStatus(outletsData));
    setCookieData();
  }

  function setCookieData() {
    let accountNumber = businessData?.accountUUID;
    let newCookieData = cookies?.outletStatus || {};
    newCookieData = { ...newCookieData, [accountNumber]: true };
    let expiry = moment().add(15, "m").toDate();
    setCookie(outletStatus, newCookieData, {
      expires: expiry,
      path: "/",
    });
  }

  /**
   * This function receives branchId and a map of selected outlets in {branchId: {outletId: {...}, }, } form,
   * and returns a new map after adding all the outlets of the branch with id as branchId.
   * @param branchId
   * @param selectedOutletsMap
   */
  function addAllOutletsForBranch(branchId: string, selectedOutletsMap: any) {
    let dataToReturn = cloneDeep(selectedOutletsMap) || {};
    outletGroupByBranchMap[branchId]?.length &&
      outletGroupByBranchMap[branchId].forEach((outletObj: any) => {
        set(dataToReturn, `${branchId}.${outletObj?.id}`, outletObj);
      });
    return dataToReturn;
  }

  /*if app source is pos and it is non admin user, 
    set selected branches as current branch */
  function handleSelectedBranch() {
    if (isSourcePos() && !isAdminUser()) {
      setSelectedBranches([businessData?.branchId]);
    }
  }

  return (
    <div
      className={`outlet-status-page mt-[2px] ${
        bulkActionConfig?.active
          ? "pt-[171px] sm:pt-[156px]"
          : "pt-[106px] sm:pt-[80px]"
      } sm:mt-[0px]`}
    >
      <OutletActionHeader
        setSearchData={setSearchData}
        setSelectedBranches={setSelectedBranches}
        setBulkActionConfig={setBulkActionConfig}
        bulkActionConfig={bulkActionConfig}
        addAllOutletsForBranch={addAllOutletsForBranch}
        outletGroupByBranchMap={outletGroupByBranchMap}
        setIsAddNewConfigModalOpen={setIsAddNewConfigModalOpen}
        getBranchOutletsCount={getBranchOutletsCount}
        getSearchResults={getSearchResults}
        searchData={searchData}
        selectedBranches={selectedBranches}
        flatBranchOutletsGroup={flatBranchOutletsGroup}
      />
      <OutletViewComponent
        searchData={searchData}
        selectedBranches={selectedBranches}
        setBulkActionConfig={setBulkActionConfig}
        addAllOutletsForBranch={addAllOutletsForBranch}
        outletGroupByBranchMap={outletGroupByBranchMap}
        bulkActionConfig={bulkActionConfig}
        setApiStateModalData={setApiStateModalData}
        updatedOutletsConfig={updatedOutletsConfig}
        getBranchOutletsCount={getBranchOutletsCount}
        getSearchResults={getSearchResults}
        statusLoading={loading}
      />
      {bulkActionConfig?.active && bulkActionConfig?.showConfirmationModal ? (
        <BulkActionConfirmationModal
          bulkActionConfig={bulkActionConfig}
          setBulkActionConfig={setBulkActionConfig}
          setApiStateModalData={setApiStateModalData}
          setUpdatedOutletsConfig={setUpdatedOutletsConfig}
          flatBranchOutletsGroup={flatBranchOutletsGroup}
        />
      ) : null}
      {isAddNewConfigModalOpen ? (
        <OutletConfigurationEditModal
          onModalClose={() => setIsAddNewConfigModalOpen(false)}
          setApiStateModalData={setApiStateModalData}
        />
      ) : null}
      {apiStateModalData?.show ? (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={apiStateModalData?.show}
          handleClose={onApiStateModalClose}
          mainText={apiStateModalData?.modalText || ""}
        ></ApiStateModal>
      ) : null}
    </div>
  );
};

export default OutletStatusComponent;
